import React from "react";
import { Link } from "react-router-dom";
import { SectionHeader } from "@/components/shared/Common";
import { Button } from "@/components/ui/button";
import { ShieldCheck, AlertTriangle, XOctagon, Clock, MessageCircle } from "lucide-react";
import ShareOnWhatsApp from "@/components/shared/ShareOnWhatsApp";

const STRIKES = [
  {
    icon: AlertTriangle,
    color: "amber",
    strike: "Strike 1 · Warning",
    what: "One confirmed no-show, contract failure, or serious conduct complaint filed by a client.",
    action: "QA status set to WARNED. Written notice on record. Pro can still take work.",
  },
  {
    icon: Clock,
    color: "orange",
    strike: "Strike 2 · Training required",
    what: "A second confirmed incident within any 12-month rolling window.",
    action: "QA status set to TRAINING REQUIRED. Profile hidden from anonymous company search for 14 days. Pro must submit a corrective plan before returning to search.",
  },
  {
    icon: XOctagon,
    color: "red",
    strike: "Strike 3 · Suspended",
    what: "A third confirmed incident, or a single serious integrity breach (e.g., forged documents, on-site aggression).",
    action: "Account SUSPENDED. Profile fully removed from search. Pending 14-day appeal window, decision by admin.",
  },
];

const CANCELLATION = [
  { window: "> 48 hours before shift start", pro: "No penalty — usual cancellation.", company: "No penalty." },
  { window: "12 – 48 hours before shift", pro: "Half-day rate forfeited to standby pool.", company: "Half booking fee still due." },
  { window: "< 12 hours before shift", pro: "Full-day rate forfeited + Strike 1 recorded.", company: "Full booking fee due to standby pool." },
  { window: "No-show / did not turn up", pro: "Strike 1 recorded immediately + auto-QA review.", company: "Booking fee refunded on report; SecuSource assists finding replacement." },
];

export default function ReliabilityPolicy() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-10">
      <div>
        <div className="inline-flex items-center gap-2 bg-gold-50 text-gold-600 border border-gold-200 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-4">
          <ShieldCheck className="w-3.5 h-3.5" /> Enforced by the platform · Same for everyone
        </div>
        <h1 className="text-4xl sm:text-5xl font-outfit font-bold text-white tracking-tight leading-[1.05]">
          Reliability &amp; Contract Policy
        </h1>
        <p className="text-white/70 mt-4 font-manrope text-base sm:text-lg">
          SecuSource exists to raise standards in the security industry. This policy is enforced automatically by the platform — every pro, every team, every booking, no exceptions.
          Both companies and professionals see the same rules before any booking is confirmed.
        </p>
      </div>

      {/* Strike ladder */}
      <section>
        <SectionHeader eyebrow="The strike ladder" title="Three-strike escalation, transparent for both sides." />
        <div className="space-y-3 mt-2">
          {STRIKES.map((s) => (
            <div key={s.strike} className={`card-flat p-5 border-l-4 border-l-${s.color}-500`} data-testid={`strike-${s.strike.split(' ')[1]}`}>
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-md bg-${s.color}-50 text-${s.color}-600 flex items-center justify-center flex-shrink-0`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-outfit font-bold text-white">{s.strike}</div>
                  <div className="text-sm text-white/70 font-manrope mt-1"><strong className="text-white">Triggered by:</strong> {s.what}</div>
                  <div className="text-sm text-white/70 font-manrope mt-1"><strong className="text-white">Consequence:</strong> {s.action}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Cancellation policy */}
      <section>
        <SectionHeader eyebrow="Cancellation windows" title="What happens if a shift is cancelled." />
        <div className="card-flat overflow-hidden mt-2">
          <table className="w-full text-sm font-manrope">
            <thead className="bg-[#0E0E0E] text-xs uppercase tracking-widest text-white/60 font-outfit">
              <tr>
                <th className="text-left p-4">Cancellation window</th>
                <th className="text-left p-4">Professional</th>
                <th className="text-left p-4">Company</th>
              </tr>
            </thead>
            <tbody>
              {CANCELLATION.map((row, i) => (
                <tr key={row.window} className={i % 2 ? "bg-[#0E0E0E]/40" : ""}>
                  <td className="p-4 text-white font-semibold align-top">{row.window}</td>
                  <td className="p-4 text-white/85 align-top">{row.pro}</td>
                  <td className="p-4 text-white/85 align-top">{row.company}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* References */}
      <section className="card-flat p-6">
        <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2">Verified before you appear</div>
        <h3 className="text-2xl font-outfit font-bold text-white mb-3">Every professional provides at least one professional reference.</h3>
        <p className="text-white/70 font-manrope text-sm leading-relaxed">
          On registration, every security professional lists at least one previous employer as a reference — company name, referee name, phone, email, and the period worked.
          SecuSource emails the referee directly and asks them to confirm the working relationship and rate the pro&apos;s reliability, timekeeping, conduct and integrity.
          A profile carries a <strong className="text-gold-500">Reference verified</strong> badge only once an independent referee has confirmed. References are private to SecuSource admin — companies see the badge, not the referee&apos;s details.
        </p>
      </section>

      {/* Appeals */}
      <section className="card-flat p-6">
        <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2">Right of appeal</div>
        <h3 className="text-2xl font-outfit font-bold text-white mb-3">Every strike can be appealed. Once.</h3>
        <p className="text-white/70 font-manrope text-sm leading-relaxed">
          Any professional or team under an active QA state can submit one written appeal from their dashboard within 14 days.
          Appeals are reviewed by SecuSource admin and the client involved. Genuine misunderstandings are cleared without penalty.
          Repeat or vexatious appeals are dismissed at admin discretion.
        </p>
      </section>

      {/* CTAs */}
      <div className="rounded-xl bg-navy-900 text-white p-8 flex flex-wrap items-center justify-between gap-6">
        <div className="max-w-xl">
          <div className="font-outfit font-bold text-2xl leading-snug">Standards protect everyone — including you.</div>
          <p className="text-slate-300 font-manrope text-sm mt-2">Companies trust the badge. Pros get better opportunities. Everyone works safer.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button asChild className="bg-brand hover:bg-brand-hover text-white"><Link to="/register/staff" data-testid="policy-cta-staff">Register as a pro (free)</Link></Button>
          <Button asChild variant="outline" className="border-white/30 text-white hover:bg-white/10"><Link to="/faq">Read the FAQ</Link></Button>
          <ShareOnWhatsApp variant="dark" testId="policy-share" message={`🛡️ SecuSource enforces a 3-strike reliability policy for every security pro on the platform:
· Strike 1 · Warning
· Strike 2 · Hidden from search 14 days
· Strike 3 · Suspended pending appeal

Every pro also provides a verified employer reference before appearing in search.

Standards you can rely on: https://secure-staff-hub.preview.emergentagent.com/reliability-policy`} />
        </div>
      </div>
    </div>
  );
}
